import { useCallback, useEffect, useState } from 'react';

/**
 * Тема админки (ADR-21): `auto` — системная (`prefers-color-scheme`), иначе ручной выбор
 * в `data-theme` на <html> и в localStorage. Значение читается до первой отрисовки
 * инлайновым скриптом в `index.html`, поэтому вспышки светлого фона нет.
 */

export const THEME_PREFS = ['auto', 'light', 'dark'] as const;
export type ThemePref = (typeof THEME_PREFS)[number];

export const THEME_STORAGE_KEY = 'otryad.admin.theme';

function isThemePref(value: unknown): value is ThemePref {
  return typeof value === 'string' && (THEME_PREFS as readonly string[]).includes(value);
}

/** Сохранённый выбор; `auto`, если ничего не выбрано или localStorage недоступен. */
export function readThemePref(): ThemePref {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePref(raw) ? raw : 'auto';
  } catch {
    return 'auto';
  }
}

/** Пишет выбор в <html> и localStorage. `auto` снимает атрибут — работает системная тема. */
export function applyThemePref(pref: ThemePref): void {
  const root = document.documentElement;
  if (pref === 'auto') root.removeAttribute('data-theme');
  else root.dataset.theme = pref;
  try {
    if (pref === 'auto') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // приватное окно или запрет на хранение — тема останется до перезагрузки
  }
}

/** Какая тема показывается сейчас (для подписи переключателя). */
export function resolvedTheme(pref: ThemePref): 'light' | 'dark' {
  if (pref !== 'auto') return pref;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Цвета графиков (Stage 13): Recharts красит SVG-атрибутами, а в них `var(--…)` не работает —
 * поэтому значения токенов читаются из вычисленных стилей и обновляются при смене темы.
 */
export const CHART_TOKENS = {
  accent: '--c-accent',
  success: '--c-success-solid',
  danger: '--c-danger',
  muted: '--c-muted',
  border: '--c-border',
  surface: '--c-surface',
  fg: '--c-fg',
} as const;

export type ChartColors = Record<keyof typeof CHART_TOKENS, string>;

const CHART_FALLBACK: ChartColors = {
  accent: '#f2a33c',
  success: '#10b981',
  danger: '#c62828',
  muted: '#67707d',
  border: '#e5e7eb',
  surface: '#ffffff',
  fg: '#16181d',
};

export function readChartColors(): ChartColors {
  if (typeof window === 'undefined') return CHART_FALLBACK;
  const style = getComputedStyle(document.documentElement);
  const out = { ...CHART_FALLBACK };
  for (const [key, token] of Object.entries(CHART_TOKENS)) {
    const value = style.getPropertyValue(token).trim();
    if (value) out[key as keyof ChartColors] = value;
  }
  return out;
}

/** Цвета графиков с подпиской на смену темы (`data-theme` и системная настройка). */
export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(() => readChartColors());

  useEffect(() => {
    const sync = () => setColors(readChartColors());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener('change', sync);
    return () => {
      observer.disconnect();
      media?.removeEventListener('change', sync);
    };
  }, []);

  return colors;
}

export function useTheme(): {
  pref: ThemePref;
  resolved: 'light' | 'dark';
  setPref: (next: ThemePref) => void;
} {
  const [pref, setPrefState] = useState<ThemePref>(() => readThemePref());
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolvedTheme(pref));

  // При `auto` тема следует за системой без перезагрузки
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const sync = () => setResolved(resolvedTheme(readThemePref()));
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  const setPref = useCallback((next: ThemePref) => {
    applyThemePref(next);
    setPrefState(next);
    setResolved(resolvedTheme(next));
  }, []);

  return { pref, resolved, setPref };
}
